import type { DebouncedFunction } from 'debounce';
import debounce from 'debounce';
import type { RefObject } from 'react';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';

import { AI_CHAT_INPUT } from '~constants/events';

export const useCustomChatInputEventDispatcher = (
	input: RefObject<HTMLInputElement | HTMLTextAreaElement | null>
) => {
	useEffect(() => {
		if (!input?.current || !isMobile) return;

		const refHolder = input.current;

		const dispatch = (focused: boolean) => {
			document.dispatchEvent(
				new CustomEvent(AI_CHAT_INPUT, {
					detail: {
						focused,
					},
				})
			);
		};

		let prevWindowHeight: number | null = null;

		const focus = () => {
			dispatch(true);
		};

		const blur = () => {
			dispatch(false);
		};

		let resize: DebouncedFunction<(event: UIEvent) => void> | undefined =
			undefined;
		try {
			resize = debounce((event: UIEvent) => {
				if (event && event.target && 'innerHeight' in event.target) {
					if (!prevWindowHeight) {
						prevWindowHeight = event.target.innerHeight as number;
						return;
					}

					if (prevWindowHeight < (event.target.innerHeight as number)) {
						blur();
					} else {
						focus();
					}
					prevWindowHeight = event.target.innerHeight as number;
				}
			}, 1000);

			input.current.addEventListener('focus', focus);
			input.current.addEventListener('blur', blur);

			window.addEventListener('resize', resize);
		} catch (e) {
			// e
		}
		return () => {
			if (refHolder) {
				refHolder.removeEventListener('focus', focus);
				refHolder.removeEventListener('blur', blur);
				resize && window.removeEventListener('resize', resize);
			}
		};
	}, [input]);
};

export const useCustomChatInputEventListener = () => {
	const [focused, setFocused] = useState(false);

	useEffect(() => {
		// @ts-ignore
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		const handler = (event) => setFocused(event.detail?.focused);

		document.addEventListener(AI_CHAT_INPUT, handler);

		return () => {
			document.removeEventListener(AI_CHAT_INPUT, handler);
		};
	}, []);

	return focused;
};
